import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Moon, Heart, Baby, Flower2, Sun } from "lucide-react";

interface JourneySelectorProps {
  onSelectJourney: (journey: 'menstrual' | 'perimenopause' | 'menopause' | 'pregnancy' | 'postpartum' | 'ivf') => void;
}

export default function JourneySelector({ onSelectJourney }: JourneySelectorProps) {
  const journeys = [
    {
      id: 'menstrual' as const,
      title: 'Menstrual Cycle Tracking',
      description: 'Track your monthly cycle, predict periods, and understand your hormonal patterns',
      icon: Moon,
      gradient: 'from-primary/20 to-secondary/20',
      benefits: [
        'Predict your next period accurately',
        'Understand your hormonal phases',
        'Track symptoms and patterns',
        'Align with lunar cycles for deeper insights'
      ]
    },
    {
      id: 'perimenopause' as const,
      title: 'Perimenopause Journey',
      description: 'Navigate irregular cycles and changing symptoms with lunar wisdom',
      icon: Flower2,
      gradient: 'from-accent/20 to-primary/20',
      benefits: [
        'Track irregular periods and symptoms',
        'Understand hormonal fluctuations',
        'Use lunar energy for support',
        'Plan around unpredictable changes'
      ]
    },
    {
      id: 'menopause' as const,
      title: 'Menopause & Beyond',
      description: 'Track symptoms and align with lunar energy post-menstruation',
      icon: Sun,
      gradient: 'from-soft-accent/20 to-accent/20',
      benefits: [
        'Track hot flashes and symptoms',
        'Follow lunar phases for energy guidance',
        'Understand your new rhythms',
        'Honor this life transition'
      ]
    },
    {
      id: 'ivf' as const,
      title: 'IVF Journey',
      description: 'Track medications, appointments, and emotional support through IVF',
      icon: Heart,
      gradient: 'from-soft-accent/20 to-secondary/20',
      benefits: [
        'Track IVF cycle days and medications',
        'Log supportive activities',
        'Monitor physical and emotional wellbeing',
        'Manage appointments and protocols'
      ]
    },
    {
      id: 'pregnancy' as const,
      title: 'Pregnancy Tracking',
      description: 'Follow your baby\'s development week by week',
      icon: Baby,
      gradient: 'from-secondary/20 to-primary/20',
      benefits: [
        'Track baby\'s growth milestones',
        'Record pregnancy symptoms',
        'Connect with lunar cycles',
        'Journal your journey'
      ]
    },
    {
      id: 'postpartum' as const,
      title: 'Postpartum Journey',
      description: 'Track baby\'s feeding, sleep, and your recovery',
      icon: Heart,
      gradient: 'from-primary-light/20 to-secondary/20',
      benefits: [
        'Log feeding and sleep patterns',
        'Track baby milestones',
        'Monitor your recovery',
        'Journal precious moments'
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
        <CardHeader>
          <CardTitle className="text-2xl">Choose Your Tracking Journey</CardTitle>
          <CardDescription className="text-base">
            Select the path that matches where you are right now. You can change this anytime.
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        {journeys.map((journey) => {
          const Icon = journey.icon;
          return (
            <Card
              key={journey.id}
              className={`relative overflow-hidden cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${journey.gradient} opacity-50`} />
              <CardHeader className="relative">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2 mb-2">
                      <Icon className="h-5 w-5" />
                      {journey.title}
                    </CardTitle>
                    <CardDescription className="text-sm">
                      {journey.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium">What you'll get:</p>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    {journey.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-primary">•</span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                <Button 
                  onClick={() => onSelectJourney(journey.id)}
                  className="w-full"
                  size="lg"
                >
                  Start {journey.title}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
